<template><markdown-wrapper><h1 id="progressbar">ProgressBar <a class="header-anchor" href="#progressbar">&#128279;</a></h1>
<blockquote>
<p>Provide up-to-date feedback on the progress of a workflow or action with simple yet flexible progress bars.</p>
</blockquote>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="example">Example <a class="header-anchor" href="#example">&#128279;</a></h2>
<p>Default progress bar.</p>
<progress-bar-example ref="progress-bar-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">progress</span>: <span class="hljs-number">66</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- progress-bar-example.vue --&gt;</span>
</code></pre>
<h2 id="with-label">With label <a class="header-anchor" href="#with-label">&#128279;</a></h2>
<p>To ensure that the label text remains legible even for low percentages, consider use <code v-pre>min-width</code> prop.</p>
<progress-bar-with-label ref="progress-bar-with-label"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">h4</span>&gt;</span>Default Label<span class="hljs-tag">&lt;/<span class="hljs-name">h4</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress&quot;</span> <span class="hljs-attr">label</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">h4</span>&gt;</span>Custom Label<span class="hljs-tag">&lt;/<span class="hljs-name">h4</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress&quot;</span> <span class="hljs-attr">label</span> <span class="hljs-attr">label-text</span>=<span class="hljs-string">&quot;Loading......Please wait.&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">h4</span>&gt;</span>Minimum Width<span class="hljs-tag">&lt;/<span class="hljs-name">h4</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress1&quot;</span> <span class="hljs-attr">min-width</span> <span class="hljs-attr">label</span>/&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">progress1</span>: <span class="hljs-number">1</span>,
        <span class="hljs-attr">progress</span>: <span class="hljs-number">66</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- progress-bar-with-label.vue --&gt;</span>
</code></pre>
<h2 id="contextual-alternatives">Contextual alternatives <a class="header-anchor" href="#contextual-alternatives">&#128279;</a></h2>
<p>Progress bars use some of the same button and alert classes for consistent styles.</p>
<progress-bar-contextual-alternatives ref="progress-bar-contextual-alternatives"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress40&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;success&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress20&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;info&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress60&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;warning&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress80&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;danger&quot;</span>/&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">progress20</span>: <span class="hljs-number">20</span>,
        <span class="hljs-attr">progress40</span>: <span class="hljs-number">40</span>,
        <span class="hljs-attr">progress60</span>: <span class="hljs-number">60</span>,
        <span class="hljs-attr">progress80</span>: <span class="hljs-number">80</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- progress-bar-contextual-alternatives.vue --&gt;</span>
</code></pre>
<h2 id="striped">Striped <a class="header-anchor" href="#striped">&#128279;</a></h2>
<p>Uses a gradient to create a striped effect. Not available in IE9 and below.</p>
<progress-bar-striped ref="progress-bar-striped"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress40&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;success&quot;</span> <span class="hljs-attr">striped</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress20&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;info&quot;</span> <span class="hljs-attr">striped</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress60&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;warning&quot;</span> <span class="hljs-attr">striped</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress80&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;danger&quot;</span> <span class="hljs-attr">striped</span>/&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">progress20</span>: <span class="hljs-number">20</span>,
        <span class="hljs-attr">progress40</span>: <span class="hljs-number">40</span>,
        <span class="hljs-attr">progress60</span>: <span class="hljs-number">60</span>,
        <span class="hljs-attr">progress80</span>: <span class="hljs-number">80</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- progress-bar-striped.vue --&gt;</span>
</code></pre>
<h2 id="animated">Animated <a class="header-anchor" href="#animated">&#128279;</a></h2>
<p>Animate the stripes right to left. Not available in IE9 and below.</p>
<progress-bar-animated ref="progress-bar-animated"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress&quot;</span> <span class="hljs-attr">striped</span> <span class="hljs-attr">active</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">progress</span>: <span class="hljs-number">40</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- progress-bar-animated.vue --&gt;</span>
</code></pre>
<h2 id="stacked">Stacked <a class="header-anchor" href="#stacked">&#128279;</a></h2>
<p>Place multiple <code v-pre>&lt;progress-bar-stack&gt;</code> into the same <code v-pre>&lt;progress-bar&gt;</code> to stack them.</p>
<progress-bar-stacked ref="progress-bar-stacked"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar-stack</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress35&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;success&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar-stack</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress20&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;warning&quot;</span> <span class="hljs-attr">striped</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">progress-bar-stack</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;progress10&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;danger&quot;</span>/&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">progress-bar</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">progress20</span>: <span class="hljs-number">20</span>,
        <span class="hljs-attr">progress35</span>: <span class="hljs-number">35</span>,
        <span class="hljs-attr">progress10</span>: <span class="hljs-number">10</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- progress-bar-stacked.vue --&gt;</span>
</code></pre>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="progressbar-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/progressbar/ProgressBar.js">ProgressBar</a> <a class="header-anchor" href="#progressbar-1">&#128279;</a></h2>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>v-model</code></td>
<td>Number</td>
<td></td>
<td>✔</td>
<td>Current progress (0 ~ 100).</td>
</tr>
<tr>
<td><code v-pre>label</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Show label on progress bar.</td>
</tr>
<tr>
<td><code v-pre>label-text</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Custom label text.</td>
</tr>
<tr>
<td><code v-pre>min-width</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Apply a minimum width to the progress bar, useful when showing label and small current value.</td>
</tr>
<tr>
<td><code v-pre>type</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Progress bar type, support success / info / warning / danger. Or you can add custom types.</td>
</tr>
<tr>
<td><code v-pre>striped</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Apply striped style.</td>
</tr>
<tr>
<td><code v-pre>active</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Apply active to striped style.</td>
</tr>
</tbody>
</table></div><h3 id="slots">Slots <a class="header-anchor" href="#slots">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>default</code></td>
<td>Use this slot if need stacked progress bar, see example in the code panel above.</td>
</tr>
</tbody>
</table></div><h2 id="progressbarstack"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/progressbar/ProgressBarStack.js">ProgressBarStack</a> <a class="header-anchor" href="#progressbarstack">&#128279;</a></h2>
<p><code v-pre>ProgressBar</code> and <code v-pre>ProgressBarStack</code> shared same props.</p>
</markdown-wrapper></template><script>
  

  

  

  

  

  
export default {components:{'progress-bar-example':{"template":"<div class=\"markdown-live-example\">\n  <progress-bar v-model=\"progress\"/>\n</div>",
    data () {
      return {
        progress: 66
      }
    }
  },'progress-bar-with-label':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <h4>Default Label</h4>\n    <progress-bar v-model=\"progress\" label/>\n    <h4>Custom Label</h4>\n    <progress-bar v-model=\"progress\" label label-text=\"Loading......Please wait.\"/>\n    <h4>Minimum Width</h4>\n    <progress-bar v-model=\"progress1\" min-width label/>\n  </section>\n</div>",
    data () {
      return {
        progress1: 1,
        progress: 66
      }
    }
  },'progress-bar-contextual-alternatives':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <progress-bar v-model=\"progress40\" type=\"success\"/>\n    <progress-bar v-model=\"progress20\" type=\"info\"/>\n    <progress-bar v-model=\"progress60\" type=\"warning\"/>\n    <progress-bar v-model=\"progress80\" type=\"danger\"/>\n  </section>\n</div>",
    data () {
      return {
        progress20: 20,
        progress40: 40,
        progress60: 60,
        progress80: 80
      }
    }
  },'progress-bar-striped':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <progress-bar v-model=\"progress40\" type=\"success\" striped/>\n    <progress-bar v-model=\"progress20\" type=\"info\" striped/>\n    <progress-bar v-model=\"progress60\" type=\"warning\" striped/>\n    <progress-bar v-model=\"progress80\" type=\"danger\" striped/>\n  </section>\n</div>",
    data () {
      return {
        progress20: 20,
        progress40: 40,
        progress60: 60,
        progress80: 80
      }
    }
  },'progress-bar-animated':{"template":"<div class=\"markdown-live-example\">\n  <progress-bar v-model=\"progress\" striped active/>\n</div>",
    data () {
      return {
        progress: 40
      }
    }
  },'progress-bar-stacked':{"template":"<div class=\"markdown-live-example\">\n  <progress-bar>\n    <progress-bar-stack v-model=\"progress35\" type=\"success\"/>\n    <progress-bar-stack v-model=\"progress20\" type=\"warning\" striped/>\n    <progress-bar-stack v-model=\"progress10\" type=\"danger\"/>\n  </progress-bar>\n</div>",
    data () {
      return {
        progress20: 20,
        progress35: 35,
        progress10: 10
      }
    }
  }}}</script>